import { lazy } from 'react';

/**
 * Lazy-loaded route components grouped here so `App.js` stays slim. Every
 * view except `TodayView` (always rendered eagerly under `/`) ships as its
 * own code-split chunk and is fetched on first navigation. `Suspense` +
 * `RouteFallback` in `App.js` handles the in-flight render.
 *
 * Each entry registers a tuple of `[LazyComponent, importer]` so we can
 * also expose a `prefetchByPath` helper — used by `SidebarNavItem` on
 * `onMouseEnter` / `onFocus` to warm the chunk before the click lands.
 * The lazy import is idempotent — webpack dedupes repeated requests so
 * spam-hovering is safe.
 */

// Tuple list keeps "thunk + lazy + path" colocated. Single source of
// truth so adding a new route only requires one entry.
const ROUTES = [
  { path: '/inbox', importer: () => import('../pages/InboxView') },
  { path: '/tasks', importer: () => import('../pages/TasksView') },
  { path: '/calls', importer: () => import('../pages/CallsView') },
  { path: '/reading', importer: () => import('../pages/ReadingView') },
  { path: '/payments', importer: () => import('../pages/PaymentsView') },
  { path: '/analytics', importer: () => import('../pages/AnalyticsView') },
  { path: '/calendar', importer: () => import('../pages/CalendarView') },
  { path: '/layout-preview', importer: () => import('../pages/LayoutPreview') },
  { path: '/notes', importer: () => import('../pages/NotesView') },
  { path: '/settings', importer: () => import('../pages/SettingsView') },
  { path: '/review', importer: () => import('../pages/ReviewView') },
  { path: '/hub', importer: () => import('../pages/PolkTaskHub') },
  { path: '/templates', importer: () => import('../pages/TemplatesView') },
  { path: '/delegatables', importer: () => import('../pages/DelegatablesView') },
  { path: '/commitments', importer: () => import('../pages/CommitmentsView') },
  { path: '/settings/health', importer: () => import('../pages/SettingsHealthView') },
  { path: '/briefing', importer: () => import('../pages/BriefingPage') },
];

const importerByPath = new Map(ROUTES.map((r) => [r.path, r.importer]));

/**
 * Trigger the dynamic import for a route's chunk without rendering it.
 * Safe to call on every mouseenter — webpack memoises the import. Falls
 * back to a no-op for unknown paths (e.g. `/` which is eager-loaded).
 */
export function prefetchByPath(path) {
  if (!path) return;
  // Strip any query / hash so "/tasks?tab=today" still matches.
  const cleanPath = path.split('?')[0].split('#')[0];
  const importer = importerByPath.get(cleanPath);
  if (importer) {
    // Fire-and-forget. We don't care about the resolved module here;
    // we just want webpack to start fetching the chunk in the
    // background. Catch silently — if it fails, the eventual real
    // navigation will surface the error via the route's Suspense
    // boundary in App.js.
    importer().catch(() => { /* swallow — surfaced on real nav */ });
  }
}

// Generate the named lazy exports from the same ROUTES array so we
// don't repeat ourselves.
const lazyByName = (name, idx) => Object.defineProperty(
  ROUTES, name, { get: () => lazy(ROUTES[idx].importer) },
);

export const InboxView = lazy(ROUTES[0].importer);
export const TasksView = lazy(ROUTES[1].importer);
export const CallsView = lazy(ROUTES[2].importer);
export const ReadingView = lazy(ROUTES[3].importer);
export const PaymentsView = lazy(ROUTES[4].importer);
export const AnalyticsView = lazy(ROUTES[5].importer);
export const CalendarView = lazy(ROUTES[6].importer);
export const LayoutPreview = lazy(ROUTES[7].importer);
export const NotesView = lazy(ROUTES[8].importer);
export const SettingsView = lazy(ROUTES[9].importer);
export const ReviewView = lazy(ROUTES[10].importer);
export const PolkTaskHub = lazy(ROUTES[11].importer);
export const TemplatesView = lazy(ROUTES[12].importer);
export const DelegatablesView = lazy(ROUTES[13].importer);
export const CommitmentsView = lazy(ROUTES[14].importer);
export const SettingsHealthView = lazy(ROUTES[15].importer);
export const BriefingPage = lazy(ROUTES[16].importer);

// Silences eslint's unused-but-defined warning for the helper.
void lazyByName;
